import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import BottomNav from "../components/BottomNav";
import BackButton from "../components/BackButton";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import {
  getModule, readProgress, writeProgress, resetProgress,
  pullServerProgress, pushServerProgress, resetServerProgress,
} from "../lib/academyContent";
import { useAuth } from "../lib/auth";
import {
  BookOpen, Clock, ArrowRight, ArrowLeft, Check, GraduationCap, PlayCircle,
  Sparkles, Lightbulb, AlertTriangle, Quote as QuoteIcon, ExternalLink, Film,
  RotateCcw, ChevronRight, TrendingUp,
} from "lucide-react";

const ICONS = { TrendingUp, BookOpen, PlayCircle };

/** Rich content block renderer — matches the block shapes in academyContent.js */
function Block({ block }) {
  if (!block) return null;
  switch (block.type) {
    case "p":
      return <p className="text-zinc-300 leading-relaxed text-base">{block.text}</p>;
    case "h":
      return <h3 className="font-display font-bold text-xl tracking-tight text-white mt-6">{block.text}</h3>;
    case "list":
      return (
        <ul className="space-y-2.5">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-zinc-300 text-[15px] leading-relaxed">
              <span className="text-nexus-pink shrink-0 mt-1">▸</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "quote":
      return (
        <blockquote className="border-l-2 border-nexus-pink pl-4 py-1 my-2">
          <div className="flex items-start gap-2">
            <QuoteIcon className="h-4 w-4 text-nexus-pink shrink-0 mt-1.5" />
            <div>
              <p className="text-zinc-200 text-lg font-display italic leading-relaxed">{block.text}</p>
              {block.cite && <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-2">{block.cite}</div>}
            </div>
          </div>
        </blockquote>
      );
    case "tip":
      return (
        <div className="rounded-xl p-4 bg-emerald-500/10 border border-emerald-500/20 flex gap-3">
          <Lightbulb className="h-5 w-5 text-emerald-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-1">{block.label || "Tip"}</div>
            <div className="text-sm text-zinc-200 leading-relaxed">{block.text}</div>
          </div>
        </div>
      );
    case "warn":
      return (
        <div className="rounded-xl p-4 bg-amber-500/10 border border-amber-500/20 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">{block.label || "Warning"}</div>
            <div className="text-sm text-zinc-200 leading-relaxed">{block.text}</div>
          </div>
        </div>
      );
    case "action":
      return (
        <Link
          to={block.href}
          className="not-prose rounded-xl p-4 border border-white/10 bg-gradient-to-r from-nexus-pink/10 via-nexus-purple/5 to-nexus-blue/10 hover:border-nexus-pink/40 transition-colors flex items-center justify-between group"
          data-testid={`academy-action-${(block.cta || "").toLowerCase().replace(/\s/g, "-")}`}
        >
          <div>
            <div className="text-[10px] uppercase tracking-widest text-nexus-pink mb-1">{block.label || "Try this on Nexus"}</div>
            <div className="text-white font-semibold flex items-center gap-2">
              {block.cta || "Open"}
              <ExternalLink className="h-3.5 w-3.5" />
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-zinc-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
        </Link>
      );
    default:
      return null;
  }
}

export default function AcademyModule() {
  const { moduleId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const mod = useMemo(() => getModule(moduleId), [moduleId]);
  const [progress, setProgress] = useState(() => readProgress(moduleId));
  const [activeStepIdx, setActiveStepIdx] = useState(0);

  useEffect(() => {
    if (!mod) return;
    let cancelled = false;
    (async () => {
      // Pull server-side progress FIRST so the local cache mirrors any other device
      if (user) {
        await pullServerProgress(api);
      }
      if (cancelled) return;
      const { completed, updatedAt } = readProgress(moduleId);
      setProgress({ completed, updatedAt });
      const nextIdx = mod.steps.findIndex((s) => !completed.includes(s.id));
      setActiveStepIdx(nextIdx >= 0 ? nextIdx : 0);
    })();
    window.scrollTo({ top: 0, behavior: "smooth" });
    return () => { cancelled = true; };
  }, [moduleId, mod, user]);

  if (!mod) {
    return (
      <div className="min-h-screen grain">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center">
          <BookOpen className="h-6 w-6 mx-auto mb-3 text-zinc-500" />
          <h1 className="font-display font-bold text-3xl mb-2">Module not found</h1>
          <p className="text-zinc-500 mb-6">The tutorial you&apos;re looking for doesn&apos;t exist or has moved.</p>
          <Link to="/academy">
            <Button className="nexus-gradient text-white border-0 rounded-full h-10 px-5 font-semibold" data-testid="academy-back-to-list">
              Browse all modules
            </Button>
          </Link>
        </div>
        <BottomNav />
      </div>
    );
  }

  const step = mod.steps[activeStepIdx];
  const total = mod.steps.length;
  const completedCount = progress.completed.length;
  const pct = Math.round((completedCount / total) * 100);
  const isStepDone = progress.completed.includes(step.id);
  const isLastStep = activeStepIdx === total - 1;
  const allDone = completedCount >= total;
  const Icon = ICONS[mod.icon] || BookOpen;

  const toggleStepComplete = async () => {
    let next;
    if (isStepDone) {
      next = progress.completed.filter((id) => id !== step.id);
    } else {
      next = [...progress.completed, step.id];
    }
    writeProgress(moduleId, next);
    setProgress({ completed: next, updatedAt: new Date().toISOString() });
    if (!isStepDone && !isLastStep) {
      setTimeout(() => setActiveStepIdx((i) => i + 1), 250);
    }
    // Sync to server + celebrate if this completion just earned the badge
    if (user) {
      const resp = await pushServerProgress(api, mod, next);
      if (resp?.just_awarded) {
        toast.success(`🏆 Badge unlocked: ${mod.title}`, {
          description: "This now shows on your profile + inside every DealRoom.",
          duration: 6000,
        });
      }
    }
  };

  const goStep = (idx) => {
    if (idx < 0 || idx >= total) return;
    setActiveStepIdx(idx);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const restart = async () => {
    if (!window.confirm("Restart this module? Your progress will reset (on every device).")) return;
    setProgress({ completed: [], updatedAt: null });
    setActiveStepIdx(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (user) await resetServerProgress(api, moduleId);
    else resetProgress(moduleId);
  };

  return (
    <div className="min-h-screen grain" data-testid="academy-module-page">
      <Navbar />
      <SEO title={`${mod.title} · Nexus Academy`} description={mod.summary} path={`/academy/${mod.id}`} />
      <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-10 pt-6">
        <BackButton fallback="/academy" label="All modules" />
      </div>
      <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-10 pt-4 pb-32 space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
            <GraduationCap className="h-3.5 w-3.5 text-nexus-pink" /> Academy
            <ChevronRight className="h-3 w-3 text-zinc-700" />
            <span className={mod.tone}>{mod.tag}</span>
          </div>
          <div className="flex items-start gap-4">
            <div className={`h-14 w-14 rounded-2xl shrink-0 bg-gradient-to-br ${mod.accent} flex items-center justify-center`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter leading-tight">{mod.title}</h1>
              <p className="text-zinc-400 mt-2 text-[15px]">{mod.summary}</p>
              <div className="flex items-center gap-4 mt-3 text-[11px] uppercase tracking-widest text-zinc-500">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {mod.duration} min read</span>
                <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" /> {total} steps</span>
                {allDone && <span className="flex items-center gap-1 text-emerald-300"><Check className="h-3 w-3" /> Completed</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="glass rounded-2xl p-4" data-testid="academy-progress-bar">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">Your progress</div>
            <div className="text-[11px] font-mono text-white tabular-nums">{completedCount} / {total} · {pct}%</div>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full nexus-gradient transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          {completedCount > 0 && (
            <button
              onClick={restart}
              className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-1"
              data-testid="academy-restart-btn"
            >
              <RotateCcw className="h-2.5 w-2.5" /> Reset progress
            </button>
          )}
        </div>

        {/* Video placeholder (visible until real video ships) */}
        <div className="rounded-2xl bg-gradient-to-br from-black via-zinc-950 to-black border border-white/5 aspect-video flex items-center justify-center relative overflow-hidden" data-testid="academy-video-placeholder">
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(255,47,110,0.15), transparent 60%), radial-gradient(circle at 70% 80%, rgba(139,92,246,0.15), transparent 60%)" }} />
          <div className="text-center relative z-10">
            <Film className="h-10 w-10 mx-auto mb-3 text-zinc-600" />
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Video walk-through</div>
            <div className="font-display text-lg text-zinc-400">Landing soon · read the steps below in the meantime</div>
          </div>
        </div>

        {/* Step navigator — pill row */}
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2" data-testid="academy-step-nav">
          {mod.steps.map((s, i) => {
            const done = progress.completed.includes(s.id);
            const active = i === activeStepIdx;
            return (
              <button
                key={s.id}
                onClick={() => goStep(i)}
                className={`shrink-0 h-8 min-w-[64px] px-3 rounded-full text-[11px] font-semibold uppercase tracking-widest transition-all flex items-center gap-1.5 ${
                  active ? "nexus-gradient text-white" : done ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25" : "bg-white/5 text-zinc-400 border border-white/10 hover:text-white"
                }`}
                data-testid={`academy-step-pill-${i}`}
              >
                {done && !active && <Check className="h-2.5 w-2.5" />}
                Step {i + 1}
              </button>
            );
          })}
        </div>

        {/* Current step content */}
        <article className="glass rounded-3xl p-6 md:p-8 space-y-5" data-testid={`academy-step-${activeStepIdx}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">Step {activeStepIdx + 1} of {total}</div>
              <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-tight">{step.title}</h2>
            </div>
            {isStepDone && (
              <div className="shrink-0 h-8 px-3 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold">
                <Check className="h-3 w-3" /> Done
              </div>
            )}
          </div>
          <div className="space-y-4">
            {step.body.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>
        </article>

        {/* Bottom step actions */}
        <div className="flex flex-col sm:flex-row items-stretch gap-2">
          <Button
            onClick={() => goStep(activeStepIdx - 1)}
            disabled={activeStepIdx === 0}
            variant="outline"
            className="flex-1 h-11 rounded-full border-white/10 text-white hover:bg-white/5 disabled:opacity-40 font-semibold"
            data-testid="academy-prev-step-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Previous
          </Button>
          <Button
            onClick={toggleStepComplete}
            className={`flex-1 h-11 rounded-full font-semibold ${
              isStepDone ? "bg-white/5 border border-white/10 text-white hover:bg-white/10" : "nexus-gradient text-white border-0"
            }`}
            data-testid="academy-mark-complete-btn"
          >
            {isStepDone ? (
              <><Check className="h-4 w-4 mr-2 text-emerald-300" /> Completed · Unmark</>
            ) : isLastStep ? (
              <><Sparkles className="h-4 w-4 mr-2" /> Finish module</>
            ) : (
              <><Check className="h-4 w-4 mr-2" /> Mark step complete</>
            )}
          </Button>
          <Button
            onClick={() => goStep(activeStepIdx + 1)}
            disabled={isLastStep}
            variant="outline"
            className="flex-1 h-11 rounded-full border-white/10 text-white hover:bg-white/5 disabled:opacity-40 font-semibold"
            data-testid="academy-next-step-btn"
          >
            Next <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>

        {/* Completion CTA */}
        {allDone && (
          <div className="rounded-3xl p-6 border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 via-nexus-blue/5 to-transparent text-center" data-testid="academy-completion-card">
            <div className="h-12 w-12 mx-auto rounded-2xl bg-emerald-500/20 flex items-center justify-center mb-3">
              <Sparkles className="h-6 w-6 text-emerald-300" />
            </div>
            <div className="font-display font-bold text-2xl mb-1">Module complete</div>
            <p className="text-zinc-400 max-w-md mx-auto mb-4">You&apos;ve finished <strong>{mod.title}</strong>. Try the next one — every module compounds.</p>
            <Link to="/academy">
              <Button className="nexus-gradient text-white border-0 rounded-full h-10 px-5 font-semibold" data-testid="academy-completion-next-btn">
                Browse more modules <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
